import { parseISO, isAfter } from 'date-fns';
import {
  AssignmentAssessmentType,
  AssignmentStatus,
  IAssignmentItem,
  IAssignmentNormalizedItem,
} from '@/entities/Assignment';
import { formatLondonDateTime } from '@/shared/lib/utils/formatLondonDateTime';
import { formatLongDate } from '@/shared/lib/utils/formatLongDate';

export const parseAssessmentType = (value: string | null): AssignmentAssessmentType =>
  value === 'formative' ? 'formative' : 'summative';

export const parseStatus = (value: string | null): AssignmentStatus =>
  value === 'completed' ? 'completed' : 'active';

export const parsePage = (value: string | null): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

export const getCtaText = (
  assessmentType?: AssignmentAssessmentType | null,
  status?: AssignmentStatus | null,
) => {
  const a = (assessmentType ?? '').toLowerCase();
  const s = (status ?? '').toLowerCase();

  const isFormative = a === 'formative';
  const isSummative = a === 'summative';
  const isCompleted = s === 'completed';
  const isActive = s === 'active';

  // default
  if ((!isFormative && !isSummative) || (!isCompleted && !isActive)) {
    return 'Go to assignment';
  }

  // formative+active -> Go to assignment
  // formative+completed -> Review assignments
  // summative+active -> Go to assignment
  // summative+completed -> Review assignments
  return isCompleted ? 'Review assignments' : 'Go to assignment';
};

export const normalizeAssignmentItem = ({
  item,
  assessmentType,
  status,
}: {
  item: IAssignmentItem;
  assessmentType?: AssignmentAssessmentType;
  status?: AssignmentStatus;
}): IAssignmentNormalizedItem => ({
  module: { id: item.module.id, title: item.module.title },
  lesson: {
    id: item.lesson.id,
    groupLessonId: item.lesson.groupLessonId ?? undefined,
    groupExamResubmissionId: item.lesson.groupExamResubmissionId ?? undefined,
    title: item.lesson.title,
    order: item.lesson.order,
    createdAt: formatLondonDateTime(item.lesson.scheduledAt),
    deadline: formatLongDate(item.lesson.endDate ?? ''),
    type: item.lesson.type,
  },
  assignment: {
    id: item.assignment.id,
    title: item.assignment.title,
    description: item.assignment.description,
    attempts: item.assignment.attempts,
    attachments: item.assignment.attachments,
  },
  moduleTeacher: item?.moduleTeacher ?? null,
  lecturer: item?.lecturer ?? null,
  submissionId: item.submission?.id || null,
  grade: item.submission?.grade || null,
  assignmentStatus: item.submission?.assignmentStatus || null,
  submissionSubmittedAt: item.submission?.submittedAt
    ? formatLondonDateTime(item.submission.submittedAt)
    : null,
  retake:
    item.assignment.attempts && item.submission?.reviewCount
      ? item.assignment.attempts - item.submission.reviewCount
      : item.assignment.attempts, // remaining attempts to submit the assignment,
  turnitinData: item.submission?.turnitinData || null,
  buttonData:
    assessmentType &&
    status &&
    (item.lesson.groupLessonId || item.lesson.groupExamResubmissionId) &&
    !isAfter(parseISO(item.lesson.scheduledAt), new Date())
      ? {
          text: getCtaText(assessmentType, status),
          href: item.lesson.groupLessonId
            ? `/assignments/groupLesson/${item.lesson.groupLessonId}`
            : `/assignments/groupExamResubmission/${item.lesson.groupExamResubmissionId}`,
        }
      : undefined,
});
